import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, Loader2, RotateCcw } from "lucide-react";
import { cva } from "class-variance-authority";
import { forwardRef, useCallback, useState } from "react";

import { SPRING_PRESS, SPRING_SWAP } from "lib/ease";
import { cn } from "lib/utils";

import "./button.css";

const buttonVariants = cva("ui-button", {
  variants: {
    variant: {
      primary: "ui-button--primary",
      secondary: "ui-button--secondary",
      outline: "ui-button--outline",
      ghost: "ui-button--ghost",
      destructive: "ui-button--destructive",
      link: "ui-button--link",
      ui: "ui-button--ui",
      default: "ui-button--primary",
    },
    size: {
      xs: "ui-button--xs",
      sm: "ui-button--sm",
      md: "ui-button--md",
      lg: "ui-button--lg",
      xl: "ui-button--xl",
      icon: "ui-button--icon",
      "icon-xs": "ui-button--icon-xs",
      "icon-sm": "ui-button--icon-sm",
      "icon-lg": "ui-button--icon-lg",
      default: "ui-button--md",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
  },
});

function getRipplePoint(event, element) {
  const bounds = element.getBoundingClientRect();
  const diameter = Math.max(bounds.width, bounds.height) * 2;
  return {
    id: `${Date.now()}-${Math.random()}`,
    diameter,
    left: event.clientX - bounds.left - diameter / 2,
    top: event.clientY - bounds.top - diameter / 2,
  };
}

const Button = forwardRef(function Button(
  {
    children,
    className,
    disabled = false,
    onPointerDown,
    pressScale = 0.93,
    ripple = false,
    size = "md",
    type = "button",
    variant = "primary",
    ...props
  },
  ref,
) {
  const reduce = useReducedMotion();
  const [ripples, setRipples] = useState([]);

  const handlePointerDown = useCallback((event) => {
    if (ripple && !reduce && !disabled) {
      const nextRipple = getRipplePoint(event, event.currentTarget);
      setRipples((current) => [...current, nextRipple]);
      window.setTimeout(() => {
        setRipples((current) => current.filter((item) => item.id !== nextRipple.id));
      }, 650);
    }
    onPointerDown?.(event);
  }, [disabled, onPointerDown, reduce, ripple]);

  return (
    <motion.button
      ref={ref}
      type={type}
      disabled={disabled}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size }), className)}
      whileHover={reduce || disabled ? undefined : { y: -1 }}
      whileTap={reduce || disabled ? undefined : { scale: pressScale }}
      transition={reduce ? { duration: 0 } : SPRING_PRESS}
      onPointerDown={handlePointerDown}
      {...props}
    >
      <span className="ui-button-content">{children}</span>
      {ripples.map((item) => (
        <span
          aria-hidden="true"
          className="ui-button-ripple"
          key={item.id}
          style={{
            width: item.diameter,
            height: item.diameter,
            left: item.left,
            top: item.top,
          }}
        />
      ))}
    </motion.button>
  );
});

const stateIcons = {
  loading: <Loader2 aria-hidden="true" className="ui-button-state-icon ui-button-state-icon--spinning" size={16} />,
  success: <Check aria-hidden="true" className="ui-button-state-icon" size={16} />,
  error: <RotateCcw aria-hidden="true" className="ui-button-state-icon" size={16} />,
};

const StatefulButton = forwardRef(function StatefulButton(
  {
    children,
    errorText = "Try again",
    icon,
    loadingText = "Loading…",
    state = "idle",
    successText = "Done",
    disabled = false,
    ...props
  },
  ref,
) {
  const reduce = useReducedMotion();
  const text = state === "loading"
    ? loadingText
    : state === "success"
      ? successText
      : state === "error"
        ? errorText
        : children;
  const textKey = `${state}-${typeof text === "string" ? text : state}`;

  return (
    <Button
      ref={ref}
      aria-busy={state === "loading"}
      disabled={disabled || state === "loading"}
      {...props}
    >
      <span className="ui-stateful-button-content">
        <AnimatePresence initial={false} mode="popLayout">
          {(state !== "idle" || icon) && (
            <motion.span
              aria-hidden={state === "idle"}
              className="ui-stateful-button-icon-slot"
              initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.7, filter: "blur(6px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.7, filter: "blur(6px)" }}
              key={state === "idle" ? "idle-icon" : `${state}-icon`}
              transition={reduce ? { duration: 0.15 } : SPRING_SWAP}
            >
              {state === "idle" ? icon : stateIcons[state]}
            </motion.span>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false} mode="wait">
          <motion.span
            aria-live="polite"
            className="ui-stateful-button-text"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -10, filter: "blur(6px)" }}
            key={textKey}
            transition={reduce ? { duration: 0.15 } : SPRING_SWAP}
          >
            {text}
          </motion.span>
        </AnimatePresence>
      </span>
    </Button>
  );
});

export { Button, StatefulButton, buttonVariants };
